import React, { useMemo, useState } from 'react';
import { Box, Typography, Card, CardContent, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, IconButton } from '@mui/material';
import { KeyboardArrowUp as KeyboardArrowUpIcon, KeyboardArrowDown as KeyboardArrowDownIcon } from '@mui/icons-material';
import { useReporting } from '../ReportingContext';
import BlogFilters from './BlogFilters';

// ── Snake Stats ────────────────────────────────────────────────────────────
// A "snake" is a set of individual blogs linked together within the same
// sub-category. Traffic enters on a HEAD blog; every blog AFTER the head is a
// snake blog. This report lists those snake blogs, each attributed to its
// owning affiliate, ranked by performance (regardless of creator), and breaks
// each blog down Blog -> content(n/a) -> Offer -> Element with a full revenue
// split: Total Rev (gross payout), Admin Rev (platform residual) and Aff Rev
// (affiliate share). RPV here = Aff Rev / Views.

const COLS = 15;

const money = (n) => `$${(Number(n) || 0).toFixed(2)}`;
const pct = (num, den) => (den > 0 ? `${((num / den) * 100).toFixed(2)}%` : '0.00%');
const rpv = (affRev, views) => `$${views > 0 ? (affRev / views).toFixed(2) : '0.00'}`;
// Truncate long values so the whole report fits on screen (full value on hover).
// Hard cut at n characters — no ellipsis.
const trunc = (v, n) => { const s = v == null ? '' : String(v); return s.length > n ? s.slice(0, n) : s; };

// Shared metric cells so every row type renders the same 9 numeric columns.
const MetricCells = ({ views, clicks, sales, totalRevenue, adminRevenue, affRevenue, showViewsOnly = false, bold = false }) => {
  const fw = bold ? 700 : 400;
  if (showViewsOnly) {
    return (
      <>
        <TableCell align="right" sx={{ fontWeight: fw }}>{(views || 0).toLocaleString()}</TableCell>
        <TableCell align="right" />
        <TableCell align="right" />
        <TableCell align="right" />
        <TableCell align="right" />
        <TableCell align="right" />
        <TableCell align="right" />
        <TableCell align="right" />
        <TableCell align="right" />
      </>
    );
  }
  return (
    <>
      <TableCell align="right" sx={{ fontWeight: fw }}>{(views || 0).toLocaleString()}</TableCell>
      <TableCell align="right" sx={{ fontWeight: fw }}>{(clicks || 0).toLocaleString()}</TableCell>
      <TableCell align="right" sx={{ fontWeight: fw }}>{pct(clicks, views)}</TableCell>
      <TableCell align="right" sx={{ fontWeight: fw }}>{(sales || 0).toLocaleString()}</TableCell>
      <TableCell align="right" sx={{ fontWeight: fw }}>{pct(sales, clicks)}</TableCell>
      <TableCell align="right" sx={{ fontWeight: fw }}>{money(totalRevenue)}</TableCell>
      <TableCell align="right" sx={{ fontWeight: fw }}>{money(adminRevenue)}</TableCell>
      <TableCell align="right" sx={{ fontWeight: fw, color: '#059669' }}>{money(affRevenue)}</TableCell>
      <TableCell align="right" sx={{ fontWeight: fw }}>{rpv(affRevenue, views)}</TableCell>
    </>
  );
};

const HEADER_CELLS = [
  { label: 'Username' },
  { label: 'Blog ID' },
  { label: 'Blog Name' },
  { label: 'Offer ID' },
  { label: 'Offer' },
  { label: 'Offer Element Name' },
  { label: 'Views', align: 'right' },
  { label: 'Clicks', align: 'right' },
  { label: 'CTR', align: 'right' },
  { label: 'Sales', align: 'right' },
  { label: 'CVR', align: 'right' },
  { label: 'Total Rev', align: 'right' },
  { label: 'Admin Rev', align: 'right' },
  { label: 'Aff Rev', align: 'right' },
  { label: 'RPV', align: 'right' },
];

const SnakeStatsTab = () => {
  const { blogStats, blogStatsLoading, blogStatsError } = useReporting();
  const [expandedBlogs, setExpandedBlogs] = useState({});

  const report = blogStats?.snake?.snakeReport;

  // Rank blogs by performance (views desc), regardless of creator. Zero-traffic
  // blogs sort to the bottom. Priority tuning lives in prebuilt.service.js and
  // is handled server-side; this is a defensive stable sort on the payload.
  const blogs = useMemo(() => {
    if (!report?.blogs) return [];
    return [...report.blogs].sort((a, b) => (b.views || 0) - (a.views || 0));
  }, [report]);

  const toggle = (id) => setExpandedBlogs((prev) => ({ ...prev, [id]: !prev[id] }));

  if (blogStatsLoading) {
    return (
      <Box>
        <BlogFilters />
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      </Box>
    );
  }

  if (blogStatsError) {
    return (
      <Box>
        <BlogFilters />
        <Typography color="error" sx={{ mb: 2 }}>{blogStatsError}</Typography>
      </Box>
    );
  }

  if (!report || blogs.length === 0) {
    return (
      <Box>
        <BlogFilters />
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
          No snake stats for this period. Snake stats appear when blogs are linked
          within a sub-category and traffic converts on a blog after the head.
        </Typography>
      </Box>
    );
  }

  const t = report.totals || {};

  return (
    <Box>
      <BlogFilters />

      <Card sx={{ mb: 4, border: '1px solid #e5e7eb', boxShadow: '0 1px 3px 0 rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
          <Box sx={{ p: 2, bgcolor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
            <Typography variant="h6" sx={{ fontSize: '1rem', fontWeight: 700, color: '#111827' }}>
              Snake Stats
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Performance of each blog in the snake position (after the head), ranked best-performing first across all creators.
            </Typography>
          </Box>

          <TableContainer component={Paper} elevation={0}>
            <Table size="small" sx={{ '& td, & th': { whiteSpace: 'nowrap' } }}>
              <TableHead sx={{ bgcolor: '#f8fafc' }}>
                <TableRow>
                  <TableCell padding="checkbox" sx={{ width: 40 }} />
                  {HEADER_CELLS.map((c) => (
                    <TableCell key={c.label} align={c.align || 'left'} sx={{ fontWeight: 600 }}>{c.label}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {/* Totals row */}
                <TableRow sx={{ bgcolor: '#dcece0' }}>
                  <TableCell padding="checkbox" />
                  <TableCell sx={{ fontWeight: 700 }}>Totals</TableCell>
                  <TableCell colSpan={5} />
                  <MetricCells
                    views={t.views}
                    clicks={t.clicks}
                    sales={t.sales}
                    totalRevenue={t.totalRevenue}
                    adminRevenue={t.adminRevenue}
                    affRevenue={t.affRevenue}
                    bold
                  />
                </TableRow>

                {blogs.map((blog) => {
                  const offers = blog.offers || [];
                  const multiOffer = offers.length > 1;
                  const isOpen = multiOffer ? !!expandedBlogs[blog.blogPostId] : true;

                  return (
                    <React.Fragment key={blog.blogPostId}>
                      {/* Blog aggregate row */}
                      <TableRow
                        hover={multiOffer}
                        onClick={multiOffer ? () => toggle(blog.blogPostId) : undefined}
                        sx={{ bgcolor: '#dcece0', cursor: multiOffer ? 'pointer' : 'default' }}
                      >
                        <TableCell padding="checkbox">
                          {multiOffer && (
                            <IconButton size="small">
                              {isOpen ? <KeyboardArrowUpIcon fontSize="small" /> : <KeyboardArrowDownIcon fontSize="small" />}
                            </IconButton>
                          )}
                        </TableCell>
                        <TableCell title={blog.affiliateUsername || ''}>{trunc(blog.affiliateUsername, 12)}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }}>{blog.blogPostId}</TableCell>
                        <TableCell sx={{ fontWeight: 600 }} title={blog.blogTitle || ''}>{blog.blogTitle ? trunc(blog.blogTitle, 50) : '—'}</TableCell>
                        <TableCell colSpan={3} />
                        <MetricCells
                          views={blog.views}
                          clicks={blog.clicks}
                          sales={blog.sales}
                          totalRevenue={blog.totalRevenue}
                          adminRevenue={blog.adminRevenue}
                          affRevenue={blog.affRevenue}
                          bold
                        />
                      </TableRow>

                      {isOpen && (
                        <>
                          {/* content (n/a) row */}
                          {blog.contentViews > 0 && (
                            <TableRow>
                              <TableCell padding="checkbox" />
                              <TableCell colSpan={3} />
                              <TableCell sx={{ color: 'text.secondary' }}>n/a</TableCell>
                              <TableCell sx={{ color: 'text.secondary' }}>content</TableCell>
                              <TableCell />
                              <MetricCells views={blog.contentViews} showViewsOnly />
                            </TableRow>
                          )}

                          {offers.map((offer) => (
                            <React.Fragment key={`${blog.blogPostId}-${offer.offerId}`}>
                              {/* Offer aggregate row */}
                              <TableRow sx={{ bgcolor: '#eef5f0' }}>
                                <TableCell padding="checkbox" />
                                <TableCell colSpan={3} />
                                <TableCell sx={{ fontWeight: 600 }} title={offer.offerId || ''}>{offer.offerDisplayId != null ? offer.offerDisplayId : (offer.offerId ? trunc(offer.offerId, 8) : '—')}</TableCell>
                                <TableCell sx={{ fontWeight: 600 }} title={offer.offerName || ''}>{offer.offerName ? trunc(offer.offerName, 30) : '—'}</TableCell>
                                <TableCell />
                                <MetricCells
                                  views={offer.views}
                                  clicks={offer.clicks}
                                  sales={offer.sales}
                                  totalRevenue={offer.totalRevenue}
                                  adminRevenue={offer.adminRevenue}
                                  affRevenue={offer.affRevenue}
                                />
                              </TableRow>

                              {/* Element rows */}
                              {(offer.elements || []).map((el) => (
                                <TableRow key={`${blog.blogPostId}-${offer.offerId}-${el.elementId}`}>
                                  <TableCell padding="checkbox" />
                                  <TableCell colSpan={5} />
                                  <TableCell sx={{ color: 'text.secondary' }} title={el.elementName || ''}>{el.elementName ? trunc(el.elementName, 30) : '—'}</TableCell>
                                  <MetricCells
                                    views={el.views}
                                    clicks={el.clicks}
                                    sales={el.sales}
                                    totalRevenue={el.totalRevenue}
                                    adminRevenue={el.adminRevenue}
                                    affRevenue={el.affRevenue}
                                  />
                                </TableRow>
                              ))}
                            </React.Fragment>
                          ))}
                        </>
                      )}
                    </React.Fragment>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
    </Box>
  );
};

export default SnakeStatsTab;
